/**
 * AES-GCM encryption for URL query parameters.
 *
 * Converts { plan, action, interval } → a single URL-safe base64 token.
 * The IV (12 bytes) is prepended to the ciphertext so each token is unique.
 *
 * Override the key via VITE_PARAMS_ENCRYPT_KEY env var (base64-encoded 32-byte key).
 * Generate one with: node -e "console.log(require('crypto').randomBytes(32).toString('base64'))"
 */

const FALLBACK_KEY = 'LeadNovaParamEncrypt012345678901'; // 32 ASCII bytes

let _cachedKey: CryptoKey | null = null;

async function getKey(): Promise<CryptoKey> {
  if (_cachedKey) return _cachedKey;

  const envKey = (import.meta.env.VITE_PARAMS_ENCRYPT_KEY as string | undefined) ?? '';
  const raw = envKey
    ? Uint8Array.from(atob(envKey), c => c.charCodeAt(0))
    : Uint8Array.from(FALLBACK_KEY, c => c.charCodeAt(0));

  _cachedKey = await crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
  return _cachedKey;
}

/**
 * Encrypt a params object into a URL-safe base64 token.
 */
export async function encryptParams(params: Record<string, string>): Promise<string> {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(params));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

  const combined = new Uint8Array(12 + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), 12);

  // base64url: no padding, + → -, / → _
  return btoa(String.fromCharCode(...combined))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Decrypt a token produced by encryptParams.
 * Returns null if the token is invalid or tampered.
 */
export async function decryptParams(token: string): Promise<Record<string, string> | null> {
  try {
    const key = await getKey();
    const base64 = token.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);
    const combined = Uint8Array.from(atob(padded), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    return JSON.parse(new TextDecoder().decode(plain));
  } catch {
    return null;
  }
}
